import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import AceEditor from 'react-ace'
import Icon from '../../../../../atoms/Icon'
import ResizableHandle from '../../QueryTool/ResizablePanels'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'

const useStyles = makeStyles(theme => ({
  expanded: {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: '1px solid var(--color-neutral-600)',
    borderRadius: '8px',
    backgroundColor: 'var(--surface-bg-color-d1)',
    overflow: 'hidden'
  },
  header: {
    padding: theme.spacing(0.75, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-400)',
    cursor: 'pointer',
    borderBottom: '1px solid var(--color-neutral-600)',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d2)'
    }
  },
  chevron: {
    fontSize: '12px',
    opacity: 0.6
  },
  toolName: {
    fontWeight: 500,
    color: 'var(--text-color-main)'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500
  },
  success: {
    backgroundColor: 'var(--color-success-100)',
    color: 'var(--color-success-700)'
  },
  error: {
    backgroundColor: 'var(--color-error-100)',
    color: 'var(--color-error-700)'
  },
  splitContainer: {
    display: 'flex',
    width: '100%',
    height: '250px',
    maxHeight: '250px',
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0
  },
  panelHeader: {
    padding: theme.spacing(0.5, 1),
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--text-color-main)',
    borderBottom: '1px solid var(--color-neutral-600)',
    backgroundColor: 'var(--surface-bg-color-d2)'
  },
  panelContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--surface-bg-color-d1)'
  },
  editor: {
    flex: 1,
    minHeight: 0
  },
  errorPanel: {
    backgroundColor: 'var(--color-error-50)',
    color: 'var(--color-error-700)'
  }
}))

function ToolCallExpanded ({ toolCall, toolResult, onCollapse }) {
  const classes = useStyles()
  const containerRef = useRef(null)
  const [leftWidthPx, setLeftWidthPx] = useState(null)

  useEffect(() => {
    if (containerRef.current && leftWidthPx === null) {
      const containerWidth = containerRef.current.offsetWidth
      if (containerWidth > 0) {
        setLeftWidthPx(containerWidth * 0.5)
      } else {
        const timeout = setTimeout(() => {
          if (containerRef.current) {
            const width = containerRef.current.offsetWidth
            if (width > 0) {
              setLeftWidthPx(width * 0.5)
            }
          }
        }, 10)
        return () => clearTimeout(timeout)
      }
    }
  }, [leftWidthPx])

  let status = 'success'
  let resultData = toolResult
  if (toolResult) {
    try {
      const parsed = typeof toolResult === 'string' ? JSON.parse(toolResult) : toolResult
      if (parsed.success !== undefined) {
        status = parsed.success ? 'success' : 'error'
        resultData = parsed.output || parsed.error || toolResult
      }
    } catch {
      // Default to success if parsing fails
    }
  }

  const requestJson = useMemo(() => {
    let json = toolCall?.args?.pipeline || toolCall?.args || {}
    if (typeof json === 'string') {
      try {
        json = JSON.parse(json)
      } catch {
        return json
      }
    }
    return JSON.stringify(json, null, 2)
  }, [toolCall])

  const responseJson = useMemo(() => {
    if (typeof resultData === 'string') {
      try {
        const parsed = JSON.parse(resultData)
        return JSON.stringify(parsed, null, 2)
      } catch {
        return resultData
      }
    }
    return JSON.stringify(resultData, null, 2)
  }, [resultData])

  const handleResize = (delta) => {
    setLeftWidthPx(prev => {
      if (!containerRef.current) return prev
      const newWidth = prev + delta
      const containerWidth = containerRef.current.offsetWidth
      const minWidth = containerWidth * 0.2
      const maxWidth = containerWidth * 0.8
      return Math.max(minWidth, Math.min(maxWidth, newWidth))
    })
  }

  return (
    <div className={classes.expanded}>
      <div className={classes.header} onClick={onCollapse}>
        <Icon name='chevronDown' className={classes.chevron} />
        <span className={classes.toolName}>{toolCall?.name || 'tool'}</span>
        <span className={`${classes.statusBadge} ${classes[status]}`}>
          {status === 'success' ? '✅' : '❌'}
          {status === 'success' ? 'Success' : 'Failed'}
        </span>
        <Icon name='chevronUp' className={classes.chevron} style={{ marginLeft: 'auto' }} />
      </div>
      <div ref={containerRef} className={classes.splitContainer}>
        {leftWidthPx !== null ? (
          <>
            <div className={classes.panel} style={{ width: `${leftWidthPx}px` }}>
              <div className={classes.panelHeader}>Request</div>
              <div className={classes.panelContent}>
                <AceEditor
                  className={classes.editor}
                  mode='json'
                  theme='xcode'
                  name={`request-${toolCall.id || 'unknown'}`}
                  readOnly
                  editorProps={{ $blockScroll: true }}
                  value={requestJson}
                  tabSize={2}
                  showPrintMargin={false}
                  showGutter
                  fontSize={12}
                  enableBasicAutocompletion={false}
                  enableLiveAutocompletion={false}
                  enableSnippets={false}
                  showFoldWidgets
                  foldStyle='markbegin'
                />
              </div>
            </div>
            <ResizableHandle direction='horizontal' onResize={handleResize} />
            <div className={`${classes.panel} ${status === 'error' ? classes.errorPanel : ''}`} style={{ flex: 1 }}>
              <div className={classes.panelHeader}>Response</div>
              <div className={classes.panelContent}>
                <AceEditor
                  className={classes.editor}
                  mode='json'
                  theme='xcode'
                  name={`response-${toolCall.id || 'unknown'}`}
                  readOnly
                  editorProps={{ $blockScroll: true }}
                  value={responseJson}
                  tabSize={2}
                  showPrintMargin={false}
                  showGutter
                  fontSize={12}
                  enableBasicAutocompletion={false}
                  enableLiveAutocompletion={false}
                  enableSnippets={false}
                  showFoldWidgets
                  foldStyle='markbegin'
                />
              </div>
            </div>
          </>
        ) : (
          <div style={{ width: '100%', padding: '16px', textAlign: 'center', color: 'var(--color-neutral-400)' }}>
            Loading...
          </div>
        )}
      </div>
    </div>
  )
}

ToolCallExpanded.propTypes = {
  toolCall: PropTypes.object.isRequired,
  toolResult: PropTypes.any,
  onCollapse: PropTypes.func.isRequired
}

export default ToolCallExpanded
